import { useEffect, useState, type ReactNode } from 'react';

import {
  fetchHomeownerById,
  selectFetchHomeownerByIdError,
  selectHasFetchedHomeownerById,
  selectHomeownerById,
  selectIsFetchingHomeownerById,
} from '@/features/homeowners';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { Button } from '@/components/ui/button';
import { ErrorNotice } from '@/components/ui/error-notice';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { AdminUserDetailsCard } from './AdminUserDetailsCard';

type UserDetailsDialogProps = {
  userId: string;
  trigger: ReactNode;
  title?: string;
  description?: string;
};

export function UserDetailsDialog({
  userId,
  trigger,
  title = 'User details',
  description = 'Review or update this user without leaving the current page.',
}: UserDetailsDialogProps) {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);
  const user = useAppSelector(selectHomeownerById(userId));
  const isLoading = useAppSelector(selectIsFetchingHomeownerById(userId));
  const error = useAppSelector(selectFetchHomeownerByIdError(userId));
  const hasFetched = useAppSelector(selectHasFetchedHomeownerById(userId));

  useEffect(() => {
    if (!open) return;
    if (user) return;
    if (isLoading) return;
    if (hasFetched && error) return;

    void dispatch(fetchHomeownerById({ userId }));
  }, [dispatch, error, hasFetched, isLoading, open, user, userId]);

  const retry = () => {
    if (isLoading) return;
    void dispatch(fetchHomeownerById({ userId }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!user ? (
          <div className="space-y-4 py-4">
            {isLoading ? (
              <div className="flex items-center gap-3">
                <LoadingSpinner />
                <span className="text-muted-foreground text-sm">Loading homeowner details…</span>
              </div>
            ) : (
              <>
                <ErrorNotice message={error} title="Failed to load homeowner" />
                {error ? (
                  <div>
                    <Button type="button" variant="outline" onClick={retry}>
                      Retry
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : (
          <AdminUserDetailsCard user={user} />
        )}
      </DialogContent>
    </Dialog>
  );
}
